import React, { useEffect, useState } from "react";
import MagicTable from "../common/MagicTable";
import Box from "../mui/Box";
import TableHeader from "../common/MagicPageHeader";
import Header from "../common/MagicTableHeader";
import { useFetchMedalWiseReportsQuery } from "../../store/apis/reportsApis";
import ImageComponent from "../mui/Image";
import DownloadToXlsxFile from "../../helper/DownloadToXlsxFile";
import { padding } from "@mui/system";
import Typography from "../mui/Typography";
import Text from "../mui/Text";

interface TabCategory {
  id: number;
  name: string;
  code: string;
}

interface MedalWiseTableProps {
  selectedTab: TabCategory | null;
  magicPageHeaderBtn: {
    text: string;
    variant: string;
    startIcon: React.ReactNode;
    onClick: () => void;
    sx: Record<string, string>;
  }[];
  magicTableHeaderBtn: any[];
  params: any;
  isInvalidSearch: boolean;
  onPaginationChange: (pageNo: number) => void;
  onSearch: (searchTerm: string) => void;
}

const MedalWiseTable: React.FC<MedalWiseTableProps> = ({
  selectedTab,
  magicTableHeaderBtn,
  params,
  isInvalidSearch,
  onPaginationChange,
  onSearch,
}) => {
  const [downloadParams, setDownloadParams] = useState<any>(null);
  const [downloadTrigger, setDownloadTrigger] = useState(false);
  const filteredParams = { ...params };
  delete filteredParams.quarterMasterId;

  const identifyColor = (value: string) => {
    if (value === "Platinum") {
      return "#E1E1E1";
    } else if (value === "Gold") {
      return "#FFF1B7";
    } else if (value === "Silver") {
      return "#F6F1FF";
    } else if (value === "Bronze") {
      return "#f9c189";
    }else{
      return "#FFFFFF";
    }
  };

  const columns = [
    {
      field: "centre",
      headerName: "Centre",
      flex: 1,
      minWidth: 300,
      sortable: true,
      renderCell: (params: any) => {
        const centre = params.row.centre || "-";
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              overflowX: "scroll",
              whiteSpace: "nowrap",
              maxWidth: "100%",
              padding:"0px 10px",
              "&::-webkit-scrollbar": {
                width: "0px",
                height: "0px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#555",
              },
            }}
          >
            {centre}
          </Box>
        );
      },
    },
    {
      field: "quarter1",
      headerName: "Quarter 1",
      flex: 1,
      minWidth: 100,
      sortable: true,
      renderCell: (params: any) => {
        return (
          <Box
            sx={{
              backgroundColor: identifyColor(params.row.quarter1),
              width: "100%",
              height: "100%",
              padding: "0px !important",
            }}
          >
            <Text
              sx={{
                height: "100%",
                textAlign: "start",
                display: "flex",
                alignItems: "center",
                padding: "0px 10px !important",
              }}
            >
              {params.row.quarter1 || "-"}
            </Text>
          </Box>
        );
      },
    },
    {
      field: "quarter2",
      headerName: "Quarter 2",
      flex: 1,
      minWidth: 100,
      sortable: true,
      renderCell: (params: any) => {
        return (
          <Box
            sx={{
              backgroundColor: identifyColor(params.row.quarter2),
              width: "100%",
              height: "100%",
              padding: "0px !important",
            }}
          >
            <Text
              sx={{
                height: "100%",
                textAlign: "start",
                display: "flex",
                alignItems: "center",
                padding: "0px 10px !important",
              }}
            >
              {params.row.quarter2 || "-"}
            </Text>
          </Box>
        );
      },
    },
    {
      field: "quarter3",
      headerName: "Quarter 3",
      flex: 1,
      minWidth: 100,
      sortable: true,
      renderCell: (params: any) => {
        return (
          <Box
            sx={{
              backgroundColor: identifyColor(params.row.quarter3),
              width: "100%",
              height: "100%",
              padding: "0px !important",
            }}
          >
            <Text
              sx={{
                height: "100%",
                textAlign: "start",
                display: "flex",
                alignItems: "center",
                padding: "0px 10px !important",
              }}
            >
              {params.row.quarter3 || "-"}
            </Text>
          </Box>
        );
      },
    },
    {
      field: "quarter4",
      headerName: "Quarter 4",
      flex: 1,
      minWidth: 100,
      sortable: true,
      renderCell: (params: any) => {
        return (
          <Box
            sx={{
              backgroundColor: identifyColor(params.row.quarter4),
              width: "100%",
              height: "100%",
              padding: "0px !important",
            }}
          >
            <Text
              sx={{
                height: "100%",
                textAlign: "start",
                display: "flex",
                alignItems: "center",
                padding: "0px 10px !important",
              }}
            >
              {params.row.quarter4 || "-"}
            </Text>
          </Box>
        );
      },
    },
  ];

  const {
    data: fetchedReports = {
      reports: [],
      totalReports: 0,
    },
    isLoading,
  } = useFetchMedalWiseReportsQuery(filteredParams);
  const { data: downloadData, isLoading: downloadLoading } =
    useFetchMedalWiseReportsQuery(downloadParams, {
      skip: !downloadParams,
    });
  const totalPages = Math.max(1, Math.ceil(fetchedReports.totalReports / 10));
  const handleDownload = async () => {
    try {
      const downloadPayload = {
        ...filteredParams,
        pageNo: "1",
        pageSize: fetchedReports.totalReports || "10",
      };

      setDownloadParams(downloadPayload);
      setDownloadTrigger(true);
    } catch (error) {
      console.error("Error during download:", error);
    }
  };

  useEffect(() => {
    if (downloadData && downloadTrigger) {
      const sheetData = [
        ["Centre", "Quarter 1", "Quarter 2", "Quarter 3", "Quarter 4"],
        ...(downloadData?.reports.map((report: any) => [
          report.centre || "-",
          report.quarter1 || "-",
          report.quarter2 || "-",
          report.quarter3 || "-",
          report.quarter4 || "-",
        ]) || []),
      ];

      DownloadToXlsxFile({
        fileNamePrifix: "Medal-wise-report",
        sheetData,
      });
      setDownloadTrigger(false);
    }
  }, [downloadData, downloadTrigger]);

  const magicPageHeaderBtn = [
    {
      text: "Download",
      variant: "outlined",
      startIcon: (
        <ImageComponent
          src="/assets/images/login/exportIcon.svg"
          alt="ExportIcon"
        />
      ),
      onClick: handleDownload,
      loading: downloadLoading,
      sx: {
        background: "white",
        border: "1px solid #D0D5DD",
        borderRadius: "8px",
        color: "black",
      },
    },
  ];
  return (
    <>
      <TableHeader
        title={`${selectedTab?.name || "Medal wise"} Report`}
        subTitle="Here you can view the report with all the details."
        buttons={magicPageHeaderBtn}
        chipLabel={`${fetchedReports?.totalReports} entries`}
      />
      <Header
        buttons={magicTableHeaderBtn}
        onSearchfieldChange={(value) => onSearch(String(value))}
        isInvalidSearch={isInvalidSearch}
      />
      <MagicTable
        columns={columns}
        rows={fetchedReports?.reports}
        loading={isLoading}
        handlePageChange={(event, pageNo) => onPaginationChange(pageNo)}
        currentPage={parseInt(params?.pageNo) || 1}
        count={totalPages}
        rowCellsSx={{ paddingLeft: "16px", textAlign: "start" }}
        headerSx={{
          paddingLeft: "16px",
          textAlign: "start",
          fontWeight: 600,
          color: "#242424",
          padding: "13px 16px",
        }}
        tableSx={{ minHeight: "300px", width: "100%" }}
        sx={{
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#DADADA",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 550,
            fontSize: "14px",
          },
          "& .MuiDataGrid-row": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
            padding: "0px",
          },
        }}
      />
    </>
  );
};

export default MedalWiseTable;
